// Authentication Utilities and Form Validation

// API base URL - Update this to match your backend
const API_BASE_URL = "https://shop.faithhomecares.co.uk/api/v1";

// Form Validation Rules
export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const validatePassword = (password) => {
  // At least 8 characters, 1 uppercase, 1 lowercase, 1 number, 1 special char
  const passwordRegex =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
  return passwordRegex.test(password);
};

export const validatePhoneNumber = (phone) => {
  const phoneRegex = /^\+?[1-9]\d{1,14}$/;
  return phoneRegex.test(phone);
};

export const validateRequired = (value) => {
  return value && value.trim().length > 0;
};

// Error Messages
export const ERROR_MESSAGES = {
  email: {
    required: "Email is required",
    invalid: "Please enter a valid email address",
  },
  password: {
    required: "Password is required",
    invalid:
      "Password must be at least 8 characters with uppercase, lowercase, number, and special character",
    mismatch: "Passwords do not match",
  },
  firstName: {
    required: "First name is required",
  },
  lastName: {
    required: "Last name is required",
  },
  phoneNumber: {
    required: "Phone number is required",
    invalid: "Please enter a valid phone number (e.g., +1234567890)",
  },
  dateOfBirth: {
    required: "Date of birth is required",
  },
  address: {
    required: "Address field is required",
  },
};

// Login Form Handler
export const handleLogin = async (formData) => {
  const errors = {};

  // Validate email
  if (!validateRequired(formData.email)) {
    errors.email = ERROR_MESSAGES.email.required;
  } else if (!validateEmail(formData.email)) {
    errors.email = ERROR_MESSAGES.email.invalid;
  }

  // Validate password
  if (!validateRequired(formData.password)) {
    errors.password = ERROR_MESSAGES.password.required;
  }

  // Return errors if validation failed
  if (Object.keys(errors).length > 0) {
    return { success: false, errors };
  }

  try {
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        email: formData.email,
        password: formData.password,
      }),
    });

    const data = await response.json();

    if (data.success) {
      // Store token in localStorage
      localStorage.setItem("authToken", data.token);

      // Store user data
      localStorage.setItem("userData", JSON.stringify(data.data));

      return {
        success: true,
        data: data.data,
        token: data.token,
      };
    } else {
      return {
        success: false,
        errors: { general: data.message || "Login failed" },
      };
    }
  } catch (error) {
    console.error("Login error:", error);
    return {
      success: false,
      errors: { general: "Network error. Please try again." },
    };
  }
};

// Register Form Handler
export const handleRegister = async (formData) => {
  const errors = {};

  // Validate first name
  if (!validateRequired(formData.firstName)) {
    errors.firstName = ERROR_MESSAGES.firstName.required;
  }

  // Validate last name
  if (!validateRequired(formData.lastName)) {
    errors.lastName = ERROR_MESSAGES.lastName.required;
  }

  // Validate email
  if (!validateRequired(formData.email)) {
    errors.email = ERROR_MESSAGES.email.required;
  } else if (!validateEmail(formData.email)) {
    errors.email = ERROR_MESSAGES.email.invalid;
  }

  // Validate password
  if (!validateRequired(formData.password)) {
    errors.password = ERROR_MESSAGES.password.required;
  } else if (!validatePassword(formData.password)) {
    errors.password = ERROR_MESSAGES.password.invalid;
  }

  // Validate confirm password
  if (formData.password !== formData.confirmPassword) {
    errors.confirmPassword = ERROR_MESSAGES.password.mismatch;
  }

  // Validate phone number
  if (!validateRequired(formData.phoneNumber)) {
    errors.phoneNumber = ERROR_MESSAGES.phoneNumber.required;
  } else if (!validatePhoneNumber(formData.phoneNumber)) {
    errors.phoneNumber = ERROR_MESSAGES.phoneNumber.invalid;
  }

  // Validate date of birth
  if (!validateRequired(formData.dateOfBirth)) {
    errors.dateOfBirth = ERROR_MESSAGES.dateOfBirth.required;
  }

  // Validate address fields
  if (!validateRequired(formData.street)) {
    errors.street = ERROR_MESSAGES.address.required;
  }
  if (!validateRequired(formData.city)) {
    errors.city = ERROR_MESSAGES.address.required;
  }
  if (!validateRequired(formData.state)) {
    errors.state = ERROR_MESSAGES.address.required;
  }
  if (!validateRequired(formData.zipCode)) {
    errors.zipCode = ERROR_MESSAGES.address.required;
  }
  if (!validateRequired(formData.country)) {
    errors.country = ERROR_MESSAGES.address.required;
  }

  // Return errors if validation failed
  if (Object.keys(errors).length > 0) {
    return { success: false, errors };
  }

  try {
    const response = await fetch(`${API_BASE_URL}/auth/register`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        password: formData.password,
        phoneNumber: formData.phoneNumber,
        dateOfBirth: formData.dateOfBirth,
        address: {
          street: formData.street,
          city: formData.city,
          state: formData.state,
          zipCode: formData.zipCode,
          country: formData.country,
        },
      }),
    });

    const data = await response.json();

    if (data.success) {
      // Store token in localStorage
      localStorage.setItem("authToken", data.token);

      // Store user data
      localStorage.setItem("userData", JSON.stringify(data.data));

      return {
        success: true,
        data: data.data,
        token: data.token,
      };
    } else {
      return {
        success: false,
        errors: { general: data.message || "Registration failed" },
      };
    }
  } catch (error) {
    console.error("Registration error:", error);
    return {
      success: false,
      errors: { general: "Network error. Please try again." },
    };
  }
};

// Admin Login Handler
export const handleAdminLogin = async (formData) => {
  const errors = {};

  // Validate email
  if (!validateRequired(formData.email)) {
    errors.email = ERROR_MESSAGES.email.required;
  } else if (!validateEmail(formData.email)) {
    errors.email = ERROR_MESSAGES.email.invalid;
  }

  // Validate password
  if (!validateRequired(formData.password)) {
    errors.password = ERROR_MESSAGES.password.required;
  }

  // Return errors if validation failed
  if (Object.keys(errors).length > 0) {
    return { success: false, errors };
  }

  try {
    const response = await fetch(`${API_BASE_URL}/admin/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        email: formData.email,
        password: formData.password,
      }),
    });

    const data = await response.json();

    if (data.success) {
      // Store admin token in localStorage
      localStorage.setItem("adminToken", data.token);

      // Store admin data
      localStorage.setItem("adminData", JSON.stringify(data.data));

      return {
        success: true,
        data: data.data,
        token: data.token,
      };
    } else {
      return {
        success: false,
        errors: { general: data.message || "Login failed" },
      };
    }
  } catch (error) {
    console.error("Admin login error:", error);
    return {
      success: false,
      errors: { general: "Network error. Please try again." },
    };
  }
};

// Forgot Password Handler
export const handleForgotPassword = async (email) => {
  const errors = {};

  // Validate email
  if (!validateRequired(email)) {
    errors.email = ERROR_MESSAGES.email.required;
  } else if (!validateEmail(email)) {
    errors.email = ERROR_MESSAGES.email.invalid;
  }

  if (Object.keys(errors).length > 0) {
    return { success: false, errors };
  }

  try {
    const response = await fetch(`${API_BASE_URL}/auth/forgotpassword`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email }),
    });

    const data = await response.json();

    if (data.success) {
      return {
        success: true,
        message: data.message,
      };
    } else {
      return {
        success: false,
        errors: { general: data.message || "Failed to send reset email" },
      };
    }
  } catch (error) {
    console.error("Forgot password error:", error);
    return {
      success: false,
      errors: { general: "Network error. Please try again." },
    };
  }
};

// Reset Password Handler
export const handleResetPassword = async (token, password, confirmPassword) => {
  const errors = {};

  // Validate password
  if (!validateRequired(password)) {
    errors.password = ERROR_MESSAGES.password.required;
  } else if (!validatePassword(password)) {
    errors.password = ERROR_MESSAGES.password.invalid;
  }

  // Validate confirm password
  if (password !== confirmPassword) {
    errors.confirmPassword = ERROR_MESSAGES.password.mismatch;
  }

  if (Object.keys(errors).length > 0) {
    return { success: false, errors };
  }

  try {
    const response = await fetch(
      `${API_BASE_URL}/auth/resetpassword/${token}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ password }),
      },
    );

    const data = await response.json();

    if (data.success) {
      // Store new token
      localStorage.setItem("authToken", data.token);

      return {
        success: true,
        message: data.message,
        token: data.token,
      };
    } else {
      return {
        success: false,
        errors: { general: data.message || "Failed to reset password" },
      };
    }
  } catch (error) {
    console.error("Reset password error:", error);
    return {
      success: false,
      errors: { general: "Network error. Please try again." },
    };
  }
};

// Update Password Handler (for logged in users)
export const handleUpdatePassword = async (
  currentPassword,
  newPassword,
  confirmPassword,
) => {
  const errors = {};

  // Validate current password
  if (!validateRequired(currentPassword)) {
    errors.currentPassword = "Current password is required";
  }

  // Validate new password
  if (!validateRequired(newPassword)) {
    errors.newPassword = ERROR_MESSAGES.password.required;
  } else if (!validatePassword(newPassword)) {
    errors.newPassword = ERROR_MESSAGES.password.invalid;
  }

  // Validate confirm password
  if (newPassword !== confirmPassword) {
    errors.confirmPassword = ERROR_MESSAGES.password.mismatch;
  }

  if (Object.keys(errors).length > 0) {
    return { success: false, errors };
  }

  try {
    const token = localStorage.getItem("authToken");
    const response = await fetch(`${API_BASE_URL}/auth/updatepassword`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        currentPassword,
        newPassword,
      }),
    });

    const data = await response.json();

    if (data.success) {
      // Store new token
      localStorage.setItem("authToken", data.token);

      return {
        success: true,
        message: data.message,
        token: data.token,
      };
    } else {
      return {
        success: false,
        errors: { general: data.message || "Failed to update password" },
      };
    }
  } catch (error) {
    console.error("Update password error:", error);
    return {
      success: false,
      errors: { general: "Network error. Please try again." },
    };
  }
};

// Logout Handler
export const handleLogout = async () => {
  try {
    const token = localStorage.getItem("authToken");

    await fetch(`${API_BASE_URL}/auth/logout`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    // Clear localStorage
    localStorage.removeItem("authToken");
    localStorage.removeItem("userData");

    return { success: true };
  } catch (error) {
    console.error("Logout error:", error);
    // Clear localStorage even if request fails
    localStorage.removeItem("authToken");
    localStorage.removeItem("userData");
    return { success: true };
  }
};

// Admin Logout Handler
export const handleAdminLogout = async () => {
  try {
    const token = localStorage.getItem("adminToken");

    await fetch(`${API_BASE_URL}/auth/logout`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    // Clear localStorage
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminData");

    return { success: true };
  } catch (error) {
    console.error("Admin logout error:", error);
    // Clear localStorage even if request fails
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminData");
    return { success: true };
  }
};

// Check if user is authenticated
export const isAuthenticated = () => {
  const token = localStorage.getItem("authToken");
  return !!token;
};

// Check if admin is authenticated
export const isAdminAuthenticated = () => {
  const token = localStorage.getItem("adminToken");
  return !!token;
};

// Get current user data
export const getCurrentUser = () => {
  const userData = localStorage.getItem("userData");
  return userData ? JSON.parse(userData) : null;
};

// Get current admin data
export const getCurrentAdmin = () => {
  const adminData = localStorage.getItem("adminData");
  return adminData ? JSON.parse(adminData) : null;
};

// Protected Route HOC (for React Router)
export const ProtectedRoute = ({ children, isAdmin = false }) => {
  const authenticated = isAdmin ? isAdminAuthenticated() : isAuthenticated();

  if (!authenticated) {
    window.location.href = isAdmin ? "/admin/login" : "/login";
    return null;
  }

  return children;
};
